import { useEffect, useState } from "react";
import encodeQueryData from "../utils/EncodeQueryData";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";

const usePagination = (
    apiServiceCall = () => {},
    filterPostRequest,
    [refreshTable, setRefreshTable],
    filterValues,
    withPagination,
    defaultSort
) => {
    const [data, setData] = useState([]);
    const [totalNbData, setTotalNbData] = useState(0);
    const [param, setParam] = useState({ page: 1, filters: filterValues ?? {} });
    const [fetching, setFetching] = useState(false);
    const [isSorted, setIsSorted] = useState(defaultSort || { column: "", val: false });
    const toast = useToastDispatchContext();

    const fetchData = async (addingMode = true) => {
        const { page, filters } = param;
        setFetching(true);
        var [response, error] = await apiServiceCall({ ...(withPagination ? { page } : {}), ...filters });
        if (error) {
            toast.error("Error Fetch", "Unable to reach data, see network section for more info");
            return;
        }

        if (withPagination == false) {
            response = { data: response, total: response.length };
        }

        var { data: rawData } = response;
        rawData = rawData.filter(filterPostRequest);
        if (response.meta) {
            response = { ...response, ...response.meta };
            delete response.meta;
        }
        setTotalNbData(response.total - (response.data.length - rawData.length));
        let finalData = [];
        finalData = param.page == 1 ? rawData : addingMode ? [...data, ...rawData] : [...data];

        if (isSorted.column != "") {
            actualiseSortAsc(finalData);
        } else {
            setData(finalData);
        }
        setFetching(false);
    };

    useEffect(() => {
        if (fetching == true) {
            return;
        }

        fetchData();
    }, [apiServiceCall, param.page, param.filters]);

    useEffect(() => {
        if (fetching == true) {
            return;
        }
        fetchData(false);
        if (refreshTable && setRefreshTable) {
            setRefreshTable(false);
        }
    }, [refreshTable]);
    const addPage = () => {
        if (fetching == true) {
            return;
        }

        setParam({ ...param, page: param.page + 1 });
    };

    const setFilter = (filter) => {
        const filters = { ...param.filters, ...filter };
        setParam({ page: 1, filters: filters });
    };

    const setFiltersAll = (filter) => {
        const filters = { ...filter };
        setParam({ page: 1, filters: filters });
    };

    const getIndex = (id) => data.findIndex((row) => row.id == id);

    const getRowByIndex = (id) => {
        let index = getIndex(id) != null;
        return index == null ? null : data[index];
    };

    const deleteRow = (id, label = "id") => {
        setTotalNbData(totalNbData - 1);
        setData(data.filter((row) => row[label] != id));
    };

    const addRow = (row) => setData([row, ...data]);

    const updateRow = (value) => {
        setData(data.map((row) => (row.id == value.id ? { ...value } : row)));
    };

    const sortData = (result) => {
        setData(result.map((row) => row));
    };

    const compareValues = (valueA, valueB) => {
        // Gérer les valeurs nulles
        if (valueA === null && valueB === null) {
            return 0;
        } else if (valueA === null) {
            return 1;
        } else if (valueB === null) {
            return -1;
        }

        // Essayer de parser les dates
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);

        if (!isNaN(dateA) && !isNaN(dateB)) {
            // Les deux sont des dates valides
            return dateA - dateB;
        }

        // Si les deux sont des nombres
        if (!isNaN(valueA) && !isNaN(valueB)) {
            return valueA - valueB;
        }

        // Comparer en tant que chaînes de caractères
        return String(valueA).localeCompare(String(valueB));
    };

    const actualiseSortAsc = (data) => {
        let name = isSorted.column;
        if (name === "" || data == null || data.length === 0) {
            return;
        }

        // Recherche de la première valeur non null sur laquelle baser le tri
        let firstNonNullValueIndex = data.findIndex(
            (item) => getObjectValueWithStringPropertyName(item, name) !== null
        );

        // Si aucune valeur non null n'est trouvée, retourner
        if (firstNonNullValueIndex === -1) {
            return;
        }

        let result = data.sort((a, b) => {
            let valueA = getObjectValueWithStringPropertyName(isSorted.val ? a : b, name);
            let valueB = getObjectValueWithStringPropertyName(isSorted.val ? b : a, name);

            return compareValues(valueA, valueB);
        });

        sortData(result);
    };

    const getObjectValueWithStringPropertyName = (item, nameProperties) => {
        let result = item;
        let hashProperties = nameProperties.split(".");
        for (let i = 0; i < hashProperties.length; i++) {
            if (result == null) continue;
            result = result[hashProperties[i]];
        }
        return result;
    };

    useEffect(() => {
        actualiseSortAsc(data);
    }, [isSorted]);

    return [
        data,
        totalNbData,
        { addPage, setFilter, setFiltersAll },
        { getIndex, getRowByIndex, deleteRow, addRow, updateRow },
        fetching,
        isSorted,
        setIsSorted,
    ];
};

export default usePagination;
